import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";

export default function Reviews() {
  const { t } = useLang();
  const fallback = t("test.items"); // used only if Google API is not configured yet
  const [reviews, setReviews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/reviews");
        if (alive) setReviews(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setReviews([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // If Google reviews are empty (no API key yet), gracefully fall back to i18n items
  const items =
    reviews && reviews.length > 0
      ? reviews.map((r) => ({
          quote: r.text,
          name: r.author_name,
          role: r.relative_time_description || "Google review",
          rating: r.rating,
          photo: r.profile_photo_url,
        }))
      : fallback.map((f) => ({ ...f, rating: 5 }));

  return (
    <section data-testid="reviews-section" className="relative py-28 lg:py-32 px-6 lg:px-10 bg-[#050505]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">
            {reviews && reviews.length > 0 ? t("test.google_eyebrow") : t("test.eyebrow")}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight">
            {t("test.title")}
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-64 bg-[#0d0d0d] border border-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {items.slice(0, 6).map((it, i) => (
              <figure
                key={i}
                data-testid={`review-${i}`}
                className="bg-[#0d0d0d] border border-white/10 rounded-xl p-8 flex flex-col justify-between"
              >
                <div>
                  <div className="flex gap-1 mb-5" data-testid={`review-stars-${i}`}>
                    {Array.from({ length: it.rating || 5 }).map((_, idx) => (
                      <Star key={idx} className="w-4 h-4 fill-[#C5A059] text-[#C5A059]" />
                    ))}
                  </div>
                  <blockquote className="font-display text-lg text-zinc-100 font-light leading-relaxed italic line-clamp-6">
                    “{it.quote}”
                  </blockquote>
                </div>
                <figcaption className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3">
                  {it.photo ? (
                    <img
                      alt={it.name}
                      src={it.photo}
                      className="w-9 h-9 rounded-full border border-white/10 object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : null}
                  <div>
                    <p className="text-white font-medium">{it.name}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{it.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
